/**
 * Como uma quantidade de insumo se **lê** neste sistema.
 *
 * <h2>O defeito que isto conserta</h2>
 *
 * A quantidade chegava do servidor como `120.000000` e ia crua para a tela,
 * para o resumo da quarentena e para a **etiqueta colada no tambor**:
 *
 * <pre>
 *   120.000000 kg
 *   300.000000 L
 * </pre>
 *
 * Em português o ponto é separador de **milhar**. `120.000000` se lê como
 * cento e vinte mil, e a etiqueta que diz isso está colada num tambor que
 * alguém vai pesar. Não é defeito de formatação — é a interface induzindo erro
 * de pesagem numa tela que decide o que vai para a formulação.
 *
 * <h2>A regra, decidida pelo PO</h2>
 *
 * <b>Vírgula, duas casas — e só mostra mais casas se houver mais casas
 * registradas.</b>
 *
 * | Registrado    | Exibido        | Por quê |
 * |---------------|----------------|---------|
 * | `120.000000`  | `120,00`       | as seis casas do banco são zeros; duas bastam |
 * | `25.500000`   | `25,50`        | idem, e o meio quilo continua visível |
 * | `0.000001`    | `0,000001`     | a precisão **registrada** aparece inteira |
 * | `1234.5`      | `1.234,50`     | milhar separado onde ele muda a leitura |
 * | `0.004`       | `0,004`        | quatro gramas **não** viram `0,00` |
 *
 * <p><b>Nunca arredonda.</b> O banco guarda `numeric(14,6)` justamente porque
 * essa precisão importa; uma pesagem de 0,004 kg exibida como `0,00` é dado que
 * some da tela sem ninguém saber que sumiu. O mínimo de duas casas é piso de
 * leitura, nunca teto de precisão.
 *
 * <h2>Por que NÃO é `Intl.NumberFormat`</h2>
 *
 * Ele resolve o grosso e tem duas armadilhas medidas neste repositório
 * (Node 20.20.2, ECMA-402 NumberFormat v3):
 *
 * <ol>
 *   <li><b>`maximumFractionDigits` é um teto que arredonda em silêncio.</b>
 *       Com `{ maximumFractionDigits: 2 }`, medido: `0.004` → `0,00`,
 *       `0.006` → `0,01` e `0.000001` → `0,00`. É exatamente o que não pode
 *       acontecer, e acontece sem erro, sem aviso e sem teste que perceba;</li>
 *   <li><b>o teto tem teto.</b> `maximumFractionDigits` aceita até <b>20</b>;
 *       21 é `RangeError`. Hoje o banco tem 6 casas e 20 sobra — mas a garantia
 *       passaria a depender do esquema do banco continuar como está, e um
 *       formatador de dado regulado não deve ter ceiling nenhum.</li>
 * </ol>
 *
 * <p>Há ainda a ponta do `Number`: `format(Number("120.000000"))` já entrega um
 * `double`, que é precisamente o que `ConfiguracaoDeSerializacao` evitou no
 * servidor mandando `BigDecimal` como **string**. Passar a string direto para o
 * `Intl` funciona no Node 20 e no Chrome 106+ (NumberFormat v3); em motor mais
 * velho ela é coagida com `ToNumber` — e a perda volta **calada**.
 *
 * <p>Por isso a conta aqui é de <b>texto</b>: separa sinal, inteiro e fração,
 * corta zero à direita, completa até duas casas e agrupa o milhar. Nenhuma
 * aritmética acontece, então não existe arredondamento a acertar — a garantia é
 * estrutural, não uma opção bem escolhida.
 */

/** Duas casas é o piso de leitura. Nunca é teto de precisão. */
export const CASAS_MINIMAS = 2;

/**
 * O que o servidor manda: `BigDecimal.toPlainString()`, sem notação científica.
 * O sinal é aceito por completude — quantidade negativa não existe no domínio,
 * e engolir o `-` seria a pior forma de descobrir que passou a existir.
 */
const DECIMAL_SIMPLES = /^([+-]?)(\d+)(?:\.(\d*))?$/;

/**
 * `U+00A0`, escrito como **escape** de propósito: espaço firme e espaço comum
 * são indistinguíveis no editor, e o próximo a passar por aqui trocaria um pelo
 * outro sem ver — a quebra só apareceria numa etiqueta já impressa.
 */
const ESPACO_FIRME = "\u00a0";

/**
 * Formata a quantidade que veio do servidor para leitura em português.
 *
 * <p>Entrada é a **string** da API — nunca um `number`. Converter para
 * `number` antes de chegar aqui joga fora a precisão que o servidor
 * atravessou a rede para preservar.
 *
 * <p>O que não casar com um decimal simples volta <b>inalterado</b>. É
 * deliberado: inventar um número a partir de uma entrada que não se entende é
 * pior do que exibir o que chegou, e um traço (`—`) esconderia um valor que
 * existe. O mesmo princípio do `EMPRESA NÃO IDENTIFICADA` da etiqueta — estado
 * estranho aparece, não some.
 */
export function formatarQuantidade(quantidade: string): string {
  const bruto = quantidade.trim();
  const partes = DECIMAL_SIMPLES.exec(bruto);
  if (partes === null) return bruto;

  // Os padrões são inalcançáveis — os grupos 1 e 2 casaram, senão `exec` teria
  // devolvido `null`. Eles estão aqui porque `noUncheckedIndexedAccess` não
  // sabe disso, e um `!` calaria o compilador sem dizer o que se sabe.
  const [, sinal = "", inteiro = "0", fracao = ""] = partes;

  // Corta só zero à DIREITA: `500000` vira `5`, e `000001` fica inteiro.
  // Nenhum dígito diferente de zero é alcançado por este corte.
  const significativas = fracao.replace(/0+$/, "");
  const decimais = significativas.padEnd(CASAS_MINIMAS, "0");

  return `${sinal}${agruparMilhar(inteiro)},${decimais}`;
}

/**
 * A quantidade com a unidade, do jeito que as três telas precisam dela.
 *
 * <p>O espaço é **firme**: na coluna estreita da tabela e nos 25,3 mm da coluna
 * de valor da etiqueta, `120,00` numa linha e `kg` na seguinte é um número sem
 * unidade no alto de uma quebra — e quantidade sem unidade não se confere.
 */
export function quantidadeComUnidade(quantidade: string, unidade: string): string {
  return `${formatarQuantidade(quantidade)}${ESPACO_FIRME}${unidade}`;
}

/**
 * Ponto a cada três dígitos, da direita para a esquerda — só na parte inteira.
 *
 * <p>`\B` impede o ponto na borda (`123` continua `123`), e o `lookahead` conta
 * os grupos até o fim do número. Vale só aqui: em português a parte decimal
 * **não** se agrupa, e `0,000001` com pontos seria outro número.
 */
function agruparMilhar(inteiro: string): string {
  return inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
